import { useState, useCallback } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

import CustomLayout from "../../../components/CustomLayout/CustomLayout";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import CustomTable from "../../../components/CustomTable/CustomTable";
import Pagination from "../../../components/CustomTable/Pagination";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import TittleLine from "../../../components/Common/TittleLine";

import BusquedaPacienteForm from "../components/BusquedaPacienteForm";

import useEmpresa from "../../../hooks/useEmpresa";
import { useMountEffect, useUpdateEffect } from "../../../hooks/LifeCycle";

import Colors from "../../../themes/Colors";
import { TIPO_DOCUMENTO_OPTIONS } from "../../../utils/LocalData";
import { isObjectEmpty } from "../../../utils/ObjectUtil";

import HojasQuirurgicasActions from "../../../stores/hospitalizacion/hojasQuirurgicas/Actions";
import {
  INITIAL_STATE_HOSPITALIZACION_FORM,
  actionsOptions,
  procesarAccionPacienteIngresado,
  columnasPacientesIngresados,
  PACIENTES_INGRESADOS_ACTIONS,
} from "../utils/PacientesIngresadosUtils";
import { ValidarFormularioFiltrosPacientesIngresados } from "../utils/ValidationUtils";

const HospitalizacionScreen = (props) => {
  const history = useHistory();

  const tipoDocumentoOptions = [
    { label: "Seleccionar tipo", value: "" },
    ...TIPO_DOCUMENTO_OPTIONS.filter((option) => option.key !== 3),
  ];

  const [formData, setFormData] = useState({
    tipoDocumento: "",
    documento: "",
    numeroIngreso: "",
    codigoPersonaHospitalizada: "",
    fechaIngreso: "",
    fechaSalida: "",
  });

  const empresa = useEmpresa();

  const [visibleActionsMenu, setVisibleActionsMenu] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [pacientes, setPacientes] = useState([]);
  const [alertConfig, setAlertConfig] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showModalValidacion, setShowModalValidacion] = useState(false);
  const [showModalYaTieneHojaQX, setShowModalYaTieneHojaQX] = useState(false);
  const [numeroIngresoValidacion, setNumeroIngresoValidacion] = useState(null);

  const buscarPacientesIngresados = (page, data) => {
    setProcessing(true);

    const errors = ValidarFormularioFiltrosPacientesIngresados(data);

    if (errors.length > 0) {
      setProcessing(false);

      setAlertConfig({
        type: "info",
        title: "¡Hola!",
        message: errors
          .map((error) => Object.values(error).join(", "))
          .join(", "),
        onConfirm: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
      return;
    }

    const params = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== "")
    );

    const payload = {
      pagina: page,
      tamano: itemsPerPage,
      numeroMedico: props.authUserData?.prestador?.codigo,
      params: Object.keys(params).length > 0 ? params : undefined,
    };
    props.consultarPacientes(payload);
  };

  const handlePageChange = (page, data) => {
    setCurrentPage(page);
    buscarPacientesIngresados(page, data);
  };

  const handleMenuClick = (action, record) => {
    setVisibleActionsMenu(null);

    if (action.value === PACIENTES_INGRESADOS_ACTIONS.VALIDACION) {
      if (record.tieneHojaQx === "S") {
        setShowModalYaTieneHojaQX(true);
        return;
      }
      
      setNumeroIngresoValidacion(record.numeroIngreso);
      setShowModalValidacion(true);
      return;
    }

    const resultado = procesarAccionPacienteIngresado({
      actionValue: action.value,
      record,
      historyPush: history.push,
      consultarPacientesData: props.consultarPacientesData,
    });

    resultado.execute();
  };

  const handleCloseModalValidacion = useCallback(() => {
    setShowModalValidacion(false);
    if (numeroIngresoValidacion) {
      history.push(`/validar-hoja-QX/${numeroIngresoValidacion}`);
    }
    setNumeroIngresoValidacion(null);
  }, [history, numeroIngresoValidacion]);

  const onStateChange =
    (key, format = null) =>
    (value) => {
      if (key === "tipoDocumento" && value === "") {
        setFormData((prevState) => {
          return {
            ...prevState,
            [key]: format ? format(value) : value,
            documento: "",
          };
        });
        return;
      }
      setFormData((prevState) => {
        return {
          ...prevState,
          [key]: format ? format(value) : value,
        };
      });
    };

  const onCleanForm = () => {
    setFormData(() => {
      return {
        ...INITIAL_STATE_HOSPITALIZACION_FORM,
      };
    });

    // Ejecutar búsqueda con filtros limpios para mostrar todos los pacientes
    buscarPacientesIngresados(1, INITIAL_STATE_HOSPITALIZACION_FORM);
    setCurrentPage(1);
  };

  useMountEffect(() => {
    setFormData((prevState) => {
      return {
        ...prevState,
      };
    });
  });

  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.consultarPacientesData) && processing) {
      setProcessing(false);

      const ingresos = props?.consultarPacientesData?.data?.ingresos || [];
      const totalRegistros =
        props?.consultarPacientesData?.data?.totalRegistros || 0;

      setPacientes(ingresos);
      setTotalRegistros(totalRegistros);

      if (totalRegistros === 0) {
        setAlertConfig({
          type: "info",
          title: "Búsqueda sin resultados",
          message: "No se encontraron registros.",
          onConfirm: () => {
            setShowAlert(false);
          },
        });
        setShowAlert(true);
      }
    }
  }, [props.consultarPacientesData]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.consultarPacientesError) && processing) {
      setPacientes([]);

      setAlertConfig({
        type: "error",
        title: "Error",
        message:
          props?.consultarPacientesError?.message ||
          "Error al obtener los pacientes ingresados",
        onConfirm: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
      setProcessing(false);
    }
  }, [props.consultarPacientesError]);

  const columns = columnasPacientesIngresados({
    empresa,
    visibleActionsMenu,
    setVisibleActionsMenu,
    handleMenuClick,
    actionsOptions,
    colors: Colors,
  });

  useMountEffect(() => {
    buscarPacientesIngresados(1, formData);
  });

  return (
    <CustomLayout
      showMenuNav={true}
      footerWidth="auto"
      marginContainer="0 0 100px 0"
      testid="pacientes-ingresados-layout"
    >
      <Section testid="title-section">
        <TittleLine
          title="Pacientes ingresados"
          weight="bold"
          testid="pacientes-ingresados-title"
        />
      </Section>

      <Section testid="search-form-section">
        <BusquedaPacienteForm
          {...formData}
          tipoDocumentoOptions={tipoDocumentoOptions}
          onStateChange={onStateChange}
          onSearch={() => {
            buscarPacientesIngresados(1, formData);
          }}
          onClean={onCleanForm}
          isLoading={processing}
          testid="busqueda-paciente-form"
        />
      </Section>

      <Section testid="table-section">
        <CustomTable
          columns={columns}
          dataSource={pacientes}
          pageSize={itemsPerPage}
          width="200%"
          testid="pacientes-table"
        />
      </Section>
      <CustomSeparator height={"20px"} testid="separator-table" />

      {totalRegistros > 0 && (
        <Section testid="pagination-section">
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalRegistros}
            onPageChange={(page) => handlePageChange(page, formData)}
            disabled={processing || props.consultarPacientesLoading}
            testid="pagination"
          />
        </Section>
      )}

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} testid="custom-alert" />
      </CheckRender>

      <CheckRender allowed={showModalValidacion}>
        <CustomAlert
          type="info"
          title="Información importante"
          message="Recuerda que puedes anexar un máximo de 10 soportes por solicitud. Si necesitas agregar más, comunícate con nuestros oficiales al 809-476-3535 para más información."
          onConfirm={handleCloseModalValidacion}
          confirmText="Entendido"
          maxWidth="500px"
          testid="alert-validacion-hoja-qx"
        />
      </CheckRender>

      <CheckRender allowed={showModalYaTieneHojaQX}>
        <CustomAlert
          type="warning"
          title="Hoja quirúrgica existente"
          message="Este ingreso ya tiene una hoja quirúrgica registrada."
          onConfirm={() => setShowModalYaTieneHojaQX(false)}
          confirmText="Entendido"
          maxWidth="500px"
          testid="alert-ya-tiene-hoja-qx"
        />
      </CheckRender>
    </CustomLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    consultarPacientesData: state.hospitalizacion.consultarPacientesData,
    consultarPacientesLoading: state.hospitalizacion.consultarPacientesLoading,
    consultarPacientesError: state.hospitalizacion.consultarPacientesError,
    authUserData: state.auth.authUserData,
    consultarTipoPssNoMedicoData: state.prestador.consultarTipoPssNoMedicoData,
  };
};

const mapDispatchToProps = {
  consultarPacientes: HojasQuirurgicasActions.consultarPacientes,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(HospitalizacionScreen);
